<template>
  <div class="invoice-status-badge">
    <Tag
      v-if="status"
      :value="statusConfig.label"
      :severity="statusConfig.severity"
      :icon="statusConfig.icon"
      class="status-tag"
    />

    <!-- Invoice details tooltip -->
    <div v-if="invoice && showDetails" class="invoice-details">
      <div v-if="invoice.invoice_number" class="detail-item">
        <span class="detail-label">{{ t('Número') }}:</span>
        <span class="detail-value">{{ invoice.invoice_number }}</span>
      </div>
      <div v-if="invoice.value" class="detail-item">
        <span class="detail-label">{{ t('Valor') }}:</span>
        <span class="detail-value">{{ formatValue(invoice.value) }}</span>
      </div>
      <div v-if="invoice.effective_date" class="detail-item">
        <span class="detail-label">{{ t('Data') }}:</span>
        <span class="detail-value">{{ formatDate(invoice.effective_date) }}</span>
      </div>
    </div>

    <!-- Action buttons -->
    <div v-if="showActions && status === 'authorized'" class="action-buttons">
      <Button
        data-testid="subaccounts-invoice-download-button"
        icon="pi pi-download"
        class="p-button-rounded p-button-text p-button-sm"
        v-tooltip.top="t('Download PDF')"
        :loading="isDownloading"
        @click="handleDownload"
      />
      <Button
        data-testid="subaccounts-invoice-send-email-button"
        icon="pi pi-envelope"
        class="p-button-rounded p-button-text p-button-sm"
        v-tooltip.top="t('Enviar por Email')"
        :loading="isSending"
        @click="handleSendEmail"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { log } from '../../../utils/DebugLogger';
import { usePaymentInvoices } from '../../../composables/usePaymentInvoices';
import { useDateFormatter } from '../../../composables/useDateFormatter';

interface Invoice {
  id?: number | string;
  status?: string;
  invoice_number?: string;
  value?: number;
  effective_date?: string;
}

interface InvoiceStatusConfig {
  label: string;
  severity: string;
  icon?: string;
}

interface Props {
  status?: string | null;
  invoice?: Invoice | null;
  showDetails?: boolean;
  showActions?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  status: null,
  invoice: null,
  showDetails: false,
  showActions: false
});

const emit = defineEmits<{
  download: [invoice: Invoice];
  'send-email': [invoice: Invoice];
}>();

const { t } = useI18n();
const { formatDate: formatDateLocale } = useDateFormatter();
const { getInvoiceStatusConfig, downloadPdf, sendEmail, formatValue } = usePaymentInvoices();

// State
const isDownloading = ref<boolean>(false);
const isSending = ref<boolean>(false);

// Computed
const statusConfig = computed<InvoiceStatusConfig>(() => {
  return getInvoiceStatusConfig(props.status || props.invoice?.status) as InvoiceStatusConfig;
});

// Methods
const formatDate = (dateStr?: string | null): string => {
  if (!dateStr) return '-';
  const date = new Date(dateStr);
  return formatDateLocale(date);
};

const handleDownload = async (): Promise<void> => {
  if (!props.invoice?.id) return;

  log('FranchisesSubaccountsInvoiceStatusBadge', 'handleDownload', 'Downloading PDF', props.invoice.id);
  isDownloading.value = true;

  try {
    await downloadPdf(Number(props.invoice.id));
    emit('download', props.invoice);
  } catch (err) {
    log('FranchisesSubaccountsInvoiceStatusBadge', 'handleDownload', 'Error', err);
  } finally {
    isDownloading.value = false;
  }
};

const handleSendEmail = async (): Promise<void> => {
  if (!props.invoice?.id) return;

  log('FranchisesSubaccountsInvoiceStatusBadge', 'handleSendEmail', 'Sending email', props.invoice.id);
  isSending.value = true;

  try {
    await sendEmail(Number(props.invoice.id));
    emit('send-email', props.invoice);
  } catch (err) {
    log('FranchisesSubaccountsInvoiceStatusBadge', 'handleSendEmail', 'Error', err);
  } finally {
    isSending.value = false;
  }
};
</script>

<style scoped>
.invoice-status-badge {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
}

.status-tag {
  font-size: 0.75rem;
}

.invoice-details {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  padding: 0.5rem;
  background: var(--surface-ground);
  border-radius: 4px;
  font-size: 0.75rem;
}

.detail-item {
  display: flex;
  gap: 0.5rem;
}

.detail-label {
  color: var(--text-color-secondary);
}

.detail-value {
  font-weight: 500;
}

.action-buttons {
  display: flex;
  gap: 0.25rem;
}
</style>
